'use client';

import { type NodeProps } from '@xyflow/react';
import { NodeShell } from '../BaseNode';
import { usePipelineStore } from '@/lib/pipeline/store';
import { MirroredInput } from '../MirroredInput';

const MODELS = ['z-image-turbo', 'flux-schnell', 'sdxl-base', 'sdxl-turbo'];
const SIZE_PRESETS: { label: string; w: number; h: number }[] = [
  { label: '1024 × 1024', w: 1024, h: 1024 },
  { label: '1024 × 1536 (portrait)', w: 1024, h: 1536 },
  { label: '1536 × 1024 (landscape)', w: 1536, h: 1024 },
  { label: '832 × 1216 (2:3)', w: 832, h: 1216 },
  { label: '1216 × 832 (3:2)', w: 1216, h: 832 },
  { label: '768 × 768', w: 768, h: 768 },
  { label: '512 × 512', w: 512, h: 512 },
];

export default function GenerateNode({ id, data }: NodeProps) {
  const update = usePipelineStore((s) => s.updateNodeData);
  const rt = usePipelineStore((s) => s.runtime[id]);
  const d = data as {
    model?: string;
    width?: number;
    height?: number;
    num_inference_steps?: number;
    seed?: number | null;
  };

  const out = rt?.output as { image?: string; _seed?: number; _ms?: number } | undefined;
  const presetValue =
    SIZE_PRESETS.find((p) => p.w === d.width && p.h === d.height)?.label ?? '';

  return (
    <NodeShell id={id} type="generate">
      <label className="text-[11px] text-[var(--text-muted)] block">
        Model
        <select
          className="input nodrag w-full text-xs mt-0.5"
          value={d.model ?? 'z-image-turbo'}
          onChange={(e) => update(id, { model: e.target.value })}
        >
          {MODELS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>
      <label className="text-[11px] text-[var(--text-muted)] block">
        Size preset
        <select
          className="input nodrag w-full text-xs mt-0.5"
          value={presetValue}
          onChange={(e) => {
            const p = SIZE_PRESETS.find((x) => x.label === e.target.value);
            if (p) update(id, { width: p.w, height: p.h });
          }}
        >
          <option value="">{presetValue ? presetValue : 'custom…'}</option>
          {SIZE_PRESETS.map((p) => (
            <option key={p.label} value={p.label}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-[var(--text-muted)]">
          Width
          <MirroredInput
            type="number"
            className="input nodrag w-full text-xs mt-0.5"
            value={d.width ?? 1024}
            onCommit={(v) => update(id, { width: parseInt(v) || 1024 })}
          />
        </label>
        <label className="text-[11px] text-[var(--text-muted)]">
          Height
          <MirroredInput
            type="number"
            className="input nodrag w-full text-xs mt-0.5"
            value={d.height ?? 1024}
            onCommit={(v) => update(id, { height: parseInt(v) || 1024 })}
          />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-[var(--text-muted)]">
          Steps
          <MirroredInput
            type="number"
            className="input nodrag w-full text-xs mt-0.5"
            value={d.num_inference_steps ?? 9}
            onCommit={(v) => update(id, { num_inference_steps: parseInt(v) || 9 })}
          />
        </label>
        <label className="text-[11px] text-[var(--text-muted)]">
          Seed
          <MirroredInput
            type="number"
            className="input nodrag w-full text-xs mt-0.5"
            placeholder="random"
            value={d.seed ?? ''}
            onCommit={(v) => update(id, { seed: v ? parseInt(v) : null })}
          />
        </label>
      </div>

      {out?.image && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={out.image} alt="generated" className="rounded-lg w-full mt-1" />
      )}
      {out?._seed != null && (
        <div className="text-[10px] text-[var(--text-muted)]">
          seed {out._seed}
          {out._ms != null ? ` · ${Math.round(out._ms)}ms` : ''}
        </div>
      )}
    </NodeShell>
  );
}
